import { EN_PLURALS, EN_STRINGS } from '../i18n/en';
import type {
  LocalePack,
  PluralCategory,
  PluralForms,
  WidgetPluralKey,
  WidgetPlurals,
  WidgetStringKey,
  WidgetStrings,
} from '../i18n/types';
import {
  getLanguageCandidates,
  getLoadedLocalePack,
  normalizeLanguage,
  resolveLocalePackCode,
} from '../i18n/loader';

export type {
  LocalePack,
  PluralCategory,
  PluralForms,
  WidgetPluralKey,
  WidgetPlurals,
  WidgetStringKey,
  WidgetStrings,
};
export { loadLocalePack, registerLocalePack } from '../i18n/loader';

const RTL_LANGUAGE_PREFIXES = ['ar', 'fa', 'he', 'ur', 'ps', 'dv', 'ku', 'yi'];

const PLURAL_KEYS = Object.keys(EN_PLURALS) as WidgetPluralKey[];
const STRING_KEYS = Object.keys(EN_STRINGS) as WidgetStringKey[];

export const DEFAULT_WIDGET_STRINGS: WidgetStrings = Object.freeze({
  ...(EN_STRINGS as Record<WidgetStringKey, string>),
  locale: 'en',
  plurals: EN_PLURALS as WidgetPlurals,
});

function isPluralForms(value: unknown): value is PluralForms {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    typeof (value as PluralForms).other === 'string'
  );
}

function sanitizeOverrides(overrides?: Record<string, unknown> | null): Partial<LocalePack> {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) return {};
  const strings: LocalePack['strings'] = {};
  const plurals: Partial<WidgetPlurals> = {};
  STRING_KEYS.forEach(key => {
    const value = overrides[key];
    if (typeof value === 'string' && value.trim()) strings[key] = value;
  });
  PLURAL_KEYS.forEach(key => {
    const value = overrides[key];
    // A plain string override applies to every plural category.
    if (typeof value === 'string' && value.trim()) plurals[key] = { other: value };
    else if (isPluralForms(value)) plurals[key] = value;
  });
  return { strings, plurals };
}

/**
 * Builds the string table for `language`. English is always available; other
 * languages use the pack passed in (or one already loaded via `loadLocalePack`)
 * and fall back to English until it arrives.
 */
export function resolveWidgetStrings(
  language?: string | null,
  overrides?: Record<string, unknown> | null,
  pack?: LocalePack | null
): WidgetStrings {
  const localePack = pack ?? getLoadedLocalePack(resolveLocalePackCode(language));
  const custom = sanitizeOverrides(overrides);
  return {
    ...(EN_STRINGS as Record<WidgetStringKey, string>),
    ...(localePack?.strings || {}),
    ...(custom.strings || {}),
    locale: normalizeLanguage(language),
    plurals: {
      ...(EN_PLURALS as WidgetPlurals),
      ...(localePack?.plurals || {}),
      ...(custom.plurals || {}),
    },
  };
}

export function formatWidgetString(
  template: string,
  values: Record<string, string | number | null | undefined>
): string {
  return template.replace(/\{(\w+)\}/g, (_match, key) => {
    const value = values[key];
    if (value === null || value === undefined) return '';
    return String(value);
  });
}

function selectPluralCategory(locale: string, count: number): PluralCategory {
  try {
    return new Intl.PluralRules(locale).select(count) as PluralCategory;
  } catch {
    return count === 1 ? 'one' : 'other';
  }
}

/**
 * Picks the CLDR plural form of `key` for `count` using `Intl.PluralRules` for
 * the table's locale and interpolates `{count}` plus any extra `values`.
 */
export function formatCount(
  strings: WidgetStrings,
  key: WidgetPluralKey,
  count: number,
  values: Record<string, string | number | null | undefined> = {}
): string {
  const forms = strings.plurals?.[key] ?? EN_PLURALS[key];
  const category = selectPluralCategory(strings.locale || 'en', count);
  const template = forms[category] ?? forms.other;
  return formatWidgetString(template, { count, ...values });
}

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function parseIsoTimestamp(value: unknown): Date | null {
  if (typeof value !== 'string' || !ISO_TIMESTAMP.test(value)) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateTimeFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale || 'en', options);
  } catch {
    return new Intl.DateTimeFormat('en', options);
  }
}

/**
 * Formats a message timestamp (hour + minute) in the widget's locale. Values that
 * are not ISO 8601 (legacy history persisted as `toLocaleTimeString()` output)
 * are returned verbatim.
 */
export function formatMessageTime(timestamp: string | null | undefined, locale: string): string {
  if (!timestamp) return '';
  const date = parseIsoTimestamp(timestamp);
  if (!date) return String(timestamp);
  return dateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(date);
}

function capitalize(text: string, locale: string): string {
  if (!text) return text;
  try {
    return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
  } catch {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}

/**
 * Formats a session's last-modified date: "Today" / "Yesterday" / "3 days ago"
 * for the last week (localized via `Intl.RelativeTimeFormat`), otherwise a short
 * localized date. Non-ISO input is returned verbatim.
 */
export function formatSessionDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '';
  const date = parseIsoTimestamp(iso);
  if (!date) return String(iso);
  const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 7) {
    try {
      const relative = new Intl.RelativeTimeFormat(locale || 'en', { numeric: 'auto' });
      return capitalize(relative.format(-Math.max(0, days), 'day'), locale);
    } catch {
      // fall through to the absolute date
    }
  }
  return dateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
}

export function isRtlLanguage(language?: string | null): boolean {
  const [base] = normalizeLanguage(language).split('-');
  return RTL_LANGUAGE_PREFIXES.includes(base);
}

export function resolveTextDirection(
  language?: string | null,
  preferred?: 'ltr' | 'rtl' | 'auto'
): 'ltr' | 'rtl' {
  if (preferred === 'ltr' || preferred === 'rtl') return preferred;
  return isRtlLanguage(language) ? 'rtl' : 'ltr';
}

export { getLanguageCandidates, normalizeLanguage };
